import { Create } from "@refinedev/antd";
import { Form, Input, Select, InputNumber, Switch, Button, Space, Divider, Typography } from "antd";
import { useForm } from "@refinedev/antd";
import { useNavigate } from "react-router-dom";
import type { AITool } from "../../types/aiTools";
import { FormFieldsEditor } from "../../components/FormFieldsEditor";
import { CanvasTemplateEditor, defaultCanvasTemplate } from "../../components/CanvasTemplateEditor";
import { ActionButtonsEditor } from "../../components/ActionButtonsEditor";

const { TextArea } = Input;

export const AIToolCreate = () => {
  const navigate = useNavigate();
  const { formProps, saveButtonProps } = useForm<AITool>();

  return (
    <Create
      title="Utwórz nowe narzędzie AI"
      saveButtonProps={saveButtonProps}
      footerButtons={({ defaultButtons }) => (
        <Space>
          <Button onClick={() => navigate("/ai-tools")}>Anuluj</Button>
          {defaultButtons}
        </Space>
      )}
    >
      <Form {...formProps}
        initialValues={{
          ...formProps.initialValues,
          categoryColor: "#1890ff",
        }}
        layout="vertical"
      >
        <Divider orientation="horizontal">Podstawowe informacje</Divider>

        <Form.Item
          label="ID"
          name="id"
          rules={[
            { required: true, message: "ID jest wymagane" },
            { pattern: /^[a-z0-9_]+$/, message: "Tylko małe litery, cyfry i podkreślniki" },
          ]}
        >
          <Input placeholder="np. tool_summarizer" />
        </Form.Item>

        <Form.Item
          label="Tytuł"
          name="title"
          rules={[{ required: true, message: "Tytuł jest wymagany" }]}
        >
          <Input placeholder="np. Podsumowanie Tekstu" />
        </Form.Item>

        <Form.Item
          label="Opis"
          name="description"
          rules={[{ required: true, message: "Opis jest wymagany" }]}
        >
          <TextArea rows={2} placeholder="Krótki opis narzędzia..." />
        </Form.Item>

        <Form.Item
          label="Ikona"
          name="icon"
          rules={[{ required: true, message: "Ikona jest wymagana" }]}
        >
          <Input placeholder="np. assets/icons/summarizer.svg" />
        </Form.Item>

        <Space size="large" style={{ width: "100%" }}>
          <Form.Item
            label="Kategoria"
            name="category"
            rules={[{ required: true }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              style={{ width: 200 }}
              options={[
                { value: "content", label: "Treść" },
                { value: "translation", label: "Tłumaczenia" },
                { value: "analysis", label: "Analiza" },
                { value: "generation", label: "Generowanie" },
                { value: "automation", label: "Automatyzacja" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Kolor kategorii"
            name="categoryColor"
            rules={[{ required: true }]}
            style={{ marginBottom: 0 }}
          >
            <Input type="color" style={{ width: 100, height: 32 }} />
          </Form.Item>
        </Space>

        <Divider orientation="horizontal">Plan i limity</Divider>

        <Space size="large">
          <Form.Item
            label="Min. wymagany plan"
            name="minPlanRequired"
            initialValue={1}
            style={{ marginBottom: 0 }}
          >
            <Select style={{ width: 150 }}>
              <Select.Option value={1}>Free</Select.Option>
              <Select.Option value={2}>Pro</Select.Option>
              <Select.Option value={3}>Enterprise</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Executions/miesiąc"
            name="executionsPerMonth"
            initialValue={1000}
            style={{ marginBottom: 0 }}
          >
            <InputNumber min={0} style={{ width: 150 }} />
          </Form.Item>
        </Space>

        <Divider orientation="horizontal">Tagi</Divider>

        <Form.Item name="tags" initialValue={[]}>
          <Select
            mode="tags"
            placeholder="Dodaj tagi..."
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Divider orientation="horizontal">Formularz</Divider>

        <Space size="large" style={{ marginBottom: 16 }}>
          <Form.Item
            label="Pozwól na załączniki"
            name={["formTemplate", "allowAttachments"]}
            valuePropName="checked"
            initialValue={false}
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Linkowanie aukcji"
            name={["formTemplate", "allowAuctionLinking"]}
            valuePropName="checked"
            initialValue={false}
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="Własne profile"
            name={["formTemplate", "allowCustomProfiles"]}
            valuePropName="checked"
            initialValue={false}
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item label="Opis formularza" name={["formTemplate", "description"]}>
          <Input placeholder="Opcjonalny opis formularza..." />
        </Form.Item>

        <Form.Item
          label="Instrukcje AI"
          name="aiSystemInstruction"
          rules={[{ required: true, message: "Instrukcje AI są wymagane" }]}
        >
          <TextArea rows={7} placeholder="Instrukcje AI" />
        </Form.Item>

        <Form.Item
          label={
            <>
              Prompt (używa
              <Typography.Link href="https://docs.rs/handlebars/latest/handlebars/#built-in-helpers" target="_blank" style={{ marginLeft: 6, marginRight: 6 }}>
                Handlebars
              </Typography.Link>
              template engine)
            </>
          }
          name="aiPrompt"
          rules={[{ required: true, message: "Prompt jest wymagany" }]}
        >
          <TextArea rows={7} placeholder="Prompt wysyłany do AI" />
        </Form.Item>

        <Form.Item label="Pola formularza" name={["formTemplate", "fields"]} initialValue={[]}>
          <FormFieldsEditor />
        </Form.Item>

        <Divider orientation="horizontal">Canvas Template</Divider>

        <Form.Item name="canvasTemplate"
          initialValue={defaultCanvasTemplate}
        >
          <CanvasTemplateEditor />
        </Form.Item>

        <Divider orientation="horizontal">Action Buttons</Divider>

        <Form.Item name="actionButtons" initialValue={[]}>
          <ActionButtonsEditor />
        </Form.Item>

        <Form.Item name="lastUsedDate" initialValue={new Date().toISOString()} hidden>
          <Input />
        </Form.Item>

        <Form.Item name="usageCount" initialValue={0} hidden>
          <InputNumber />
        </Form.Item>
      </Form>
    </Create>
  );
};
